import React, { useContext, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import UserIcon from '@material-ui/icons/AccountCircleOutlined';
import { Box } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Typography } from '@swagup-com/components';
import { useSelector } from 'react-redux';
import { useHoliday } from '../../contexts/holiday';
import { useAuth } from '../global/Authentication/AuthProvider';
import ClickableTooltip from './ClickableTooltip';
import ClickableDrawer from './ClickableDrawer';
import AccountControl from './AccountControl';
import AccountProfileControl from './AccountProfileControl';
import UserAvatar from './UserAvatar';
import globalApi from '../../apis/swagup/global';
import { useProfileCore } from '../../hooks/globalQueries';
import { useBreakpointDown, useIntercomOnLeft } from '../../hooks';
import useDiscountsAndRewards from '../../hooks/useDiscountsAndRewards';
import SwagCardBalance from './SwagCardBalance';
import gtm from '../../utils/gtm';
import { useMembership } from '../../contexts/membershipContext';
import {
  CatalogTopHeaderSignedIn,
  IntegrationHeader,
  MembershipHeader,
  ModalTopHeader,
  RedeemTopHeaderWithButton,
  TitleTopHeader,
  TopHeaderTitleWithButton,
  InventoryHistoriesHeader
} from './common';
import { useLeftNavbar } from '../../contexts/leftNavbar';
import { getSearchParamsObject } from '../../helpers/utils';

const useStyles = makeStyles({
  menuIcon: { color: '#4A4F54', width: 16 },
  menuContainer: {
    cursor: 'pointer',
    width: ({ isAuthenticated }) => (isAuthenticated ? 110 : 72),
    height: 40,
    padding: 8,
    paddingRight: ({ isAuthenticated }) => (isAuthenticated ? 4 : 8),
    border: '1px solid #E5E7E8',
    borderRadius: 8,
    '&:hover': {
      boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.12), 0px 12px 24px 2px rgba(0, 0, 0, 0.06)'
    }
  },
  accountMenuContainer: {
    cursor: 'pointer',
    paddingRight: ({ isAuthenticated }) => (isAuthenticated ? 4 : 8)
  },
  membershipBox: {
    background: '#EAF1FA',
    borderRadius: 4,
    position: 'relative',
    height: '30px',
    width: '30px',
    cursor: 'pointer'
  },
  membershipImage: { objectFit: 'contain', height: '30px', width: '30px' }
});

const MenuDropdown = ({
  profile,
  currentAccount,
  logout,
  login,
  handleAccountSwap,
  isAuthenticated,
  classes,
  signInTitle
}) => {
  const isXs = useBreakpointDown('xs');
  const { currentMembership } = useDiscountsAndRewards();
  const avatar = profile ? (
    <UserAvatar name={profile.first_name} nameForColorGeneration={currentAccount?.name} size={24} />
  ) : (
    <UserIcon />
  );

  const title = <AccountControl profile={profile} onSwapAccount={handleAccountSwap} logout={logout} login={login} />;

  return (
    <ClickableTooltip title={title} placement="bottom-end" isAccountDropdown>
      <Grid container alignItems="center" justifyContent="center">
        {avatar}
        <Typography variant="body3SemiBoldInter" style={{ color: '#111827', marginLeft: '8px' }}>
          {signInTitle}
        </Typography>
        {isAuthenticated && (
          <Box className={classes.membershipBox}>
            <img src={currentMembership.image} alt={currentMembership.name} className={classes.membershipImage} />
          </Box>
        )}
      </Grid>
    </ClickableTooltip>
  );
};

const AccountMenuDropdown = ({
  profile,
  currentAccount,
  logout,
  login,
  handleAccountSwap,
  isAuthenticated,
  classes,
  setOpen,
  open
}) => {
  const isXs = useBreakpointDown('xs');
  const { currentMembership } = useDiscountsAndRewards();

  const avatar = profile ? (
    <UserAvatar name={profile.first_name} nameForColorGeneration={currentAccount?.name} size={24} />
  ) : (
    <img src="/images/public/log-in.svg" alt="Log In" />
  );

  const title = (
    <AccountProfileControl
      profile={profile}
      onSwapAccount={handleAccountSwap}
      logout={logout}
      login={login}
      setOpen={setOpen}
    />
  );

  return (
    <ClickableDrawer title={title} placement="bottom-end" isAccountDropdown open={open} setOpen={setOpen}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-around"
        className={!isXs ? classes.accountMenuContainer : undefined}
      >
        {avatar}
        <Typography style={{ color: '#111827', margin: '0px 8px 0px 8px' }}>
          {`${profile.first_name} ${profile.last_name}`}
        </Typography>
        <ExpandMoreIcon />
      </Grid>
    </ClickableDrawer>
  );
};

const AccountProfile = ({ isDashboardHeader }) => {
  const { logout, isAuthenticated } = useAuth();
  const { swagupMembershipPlans } = useFlags();
  const classes = useStyles({ isAuthenticated: isAuthenticated && swagupMembershipPlans });
  const { data: profile, isFetching, isError, refetch } = useProfileCore();
  const { currentAccount } = profile ?? {};
  const { currentMembership } = useDiscountsAndRewards(); // * This is to get user membership details on to GTM & GA4
  gtm.logInDetails(profile, currentMembership);

  useEffect(() => {
    refetch();
  }, []);

  const handleAccountSwap = async accountId => {
    await globalApi.setAccount(profile.id, { current_account: accountId });
    const newLocation = isDashboardHeader ? '/dashboard' : '/';
    window.location.assign(`${window.location.origin}${newLocation}`);
  };

  return (
    !isFetching &&
    !isError && (
      <MenuDropdown
        profile={profile}
        currentAccount={currentAccount}
        logout={logout}
        handleAccountSwap={handleAccountSwap}
        classes={classes}
        isAuthenticated={isAuthenticated && swagupMembershipPlans}
      />
    )
  );
};

const AccountDetails = ({ isDashboardHeader }) => {
  const [open, setOpen] = React.useState(false);
  const { logout, isAuthenticated } = useAuth();
  const { swagupMembershipPlans } = useFlags();
  const classes = useStyles({ isAuthenticated: isAuthenticated && swagupMembershipPlans });
  const { data: profile, isFetching, isError, refetch } = useProfileCore();
  const { currentAccount } = profile ?? {};
  const { currentMembership } = useDiscountsAndRewards(); // * This is to get user membership details on to GTM & GA4
  gtm.logInDetails(profile, currentMembership);

  useEffect(() => {
    refetch();
  }, []);

  const handleAccountSwap = async accountId => {
    await globalApi.setAccount(profile.id, { current_account: accountId });
    const newLocation = isDashboardHeader ? '/dashboard/' : '/';
    window.location.assign(`${window.location.origin}${newLocation}`);
  };

  const handleClose = () => {
    setOpen(!open);
  };

  return !isError && profile ? (
    <AccountMenuDropdown
      profile={profile}
      currentAccount={currentAccount}
      logout={logout}
      handleAccountSwap={handleAccountSwap}
      classes={classes}
      isAuthenticated={isAuthenticated && swagupMembershipPlans}
      setOpen={handleClose}
      open={open}
    />
  ) : (
    <></>
  );
};

const TopBarNavigator = pathname => {
  if (pathname.includes('/dashboard/inventory/inventory-histories/')) {
    return <TitleTopHeader title="Inventory History Details" />;
  }
  switch (pathname) {
    case '/dashboard/':
      return <TitleTopHeader title="Dashboard" />;
    case '/dashboard/orders-requested':
    case '/dashboard/orders-completed':
      return <TitleTopHeader title="Orders" />;
    case '/dashboard/inventory':
      return <TitleTopHeader title="Inventory" />;
    case '/dashboard/shipments':
      return <TitleTopHeader title="Shipments" />;
    case '/dashboard/contacts':
      return (
        <TopHeaderTitleWithButton
          title="Contacts"
          buttonText="Add Contact"
          buttonLink="/contacts"
          search="add-open=true"
        />
      );
    case '/dashboard/billing/invoices':
      return <TitleTopHeader title="Invoices" />;
    case '/dashboard/billing/add-credits':
      return <TitleTopHeader title="Billing" />;
    case '/dashboard/billing/transactions':
      return <TitleTopHeader title="Billing" />;
    case '/dashboard/settings/company':
    case '/dashboard/membership/manage-membership':
    case '/dashboard/settings/users':
    case '/dashboard/settings/profile':
    case '/dashboard/membership/manage-membership-switch':
    case '/dashboard/membership/manage-membership-cancel':
      return <TitleTopHeader title="Settings" />;
    case '/dashboard/custom-swag-packs/catalog':
    case '/dashboard/bulk/catalog':
      return <CatalogTopHeaderSignedIn />;
    case '/dashboard/products':
      const { newShoppingExperience } = useFlags();
      return (
        <>
          {newShoppingExperience ? (
            <TitleTopHeader title="My Products" />
          ) : (
            <TopHeaderTitleWithButton
              title="My Products"
              buttonText="Design Products"
              buttonLink="/product-onboarding"
              buttonLinkState={{ fromProducts: true }}
            />
          )}
        </>
      );
    case '/dashboard/shops':
      return <TopHeaderTitleWithButton title="Shops" buttonText="Build Shop" buttonLink="/shop-create" withState />;
    case '/dashboard/billing/credits':
      return <TopHeaderTitleWithButton title="Billing" buttonText="Add Credits" buttonLink="/billing/add-credits" />;
    case '/dashboard/redeem-pages':
      return <RedeemTopHeaderWithButton title="Redeem" buttonText="Create Redeem Page" />;
    case '/dashboard/integrations':
      return <IntegrationHeader title="Integrations" />;
    case '/dashboard/billing/accounts':
      return <ModalTopHeader title="Billing" buttonText="Add a Credit Card" />;
    case '/dashboard/settings/your-membership':
      return <MembershipHeader title="Settings" buttonLink="/membership/manage-membership" />;
    default:
      return null;
  }
};

const LoggedIn = ({ isDashboardHeader, pathname }) => {
  const classes = useStyles();
  const { swagupMembershipPlans, leftBarNavigation } = useFlags();
  const { currentMembership } = useMembership();
  const { data: profile } = useProfileCore();

  useIntercomOnLeft(currentMembership, profile);

  const leftNavBar = useLeftNavbar();

  const { leftNavOpen } = leftNavBar || {};

  const navbarStyle = useMemo(() => {
    return leftNavOpen ? { marginLeft: 100 } : {};
  }, [leftNavOpen]);

  return (
    <Grid container style={{ display: 'inline-flex', flexWrap: 'nowrap' }}>
      <Grid container alignItems="center" justifyContent="flex-end">
        {swagupMembershipPlans && (
          <Grid
            item
            xs
            container
            justifyContent="flex-end"
            style={{ paddingRight: 12, ...(leftBarNavigation ? navbarStyle : {}) }}
            className="titlesection"
          >
            {leftBarNavigation ? TopBarNavigator(pathname) : <SwagCardBalance />}
          </Grid>
        )}
        <Grid item>
          {leftBarNavigation ? (
            <>
              <AccountDetails isDashboardHeader={isDashboardHeader} />
            </>
          ) : (
            <AccountProfile isDashboardHeader={isDashboardHeader} />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

const LoggedOut = () => {
  const { login, isAuthenticated } = useAuth();
  const darkMode = useHoliday();
  const { swagupMembershipPlans } = useFlags();
  const classes = useStyles({ darkMode, isAuthenticated: isAuthenticated && swagupMembershipPlans });
  return (
    <Grid container alignItems="center" justifyContent="flex-end" style={{ cursor: 'pointer' }}>
      <MenuDropdown
        signInTitle="Sign In"
        login={login}
        isAuthenticated={isAuthenticated && swagupMembershipPlans}
        classes={classes}
      />
    </Grid>
  );
};

export { AccountProfile, LoggedIn, LoggedOut };
